import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi } from '@/lib/api';
import { Users, FileText, Building2, TrendingUp, ChevronRight, Target } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cdfce993-612e-4617-ac7b-89da32b19bd7/artifacts/wcwsdulx_Logo%20benchory.png";

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardApi.stats().then(r => { setStats(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
      <div className="w-8 h-8 border-3 border-[#1B2A4A]/20 border-t-[#1B2A4A] rounded-full animate-spin" />
    </div>
  );

  const cards = [
    { label: 'Établissements', value: stats?.total_clients || 0, icon: Building2, color: 'border-[#1B2A4A]', link: '/clients' },
    { label: 'Rapports générés', value: stats?.total_reports || 0, icon: FileText, color: 'border-[#B8860B]', link: '/reports' },
    { label: 'Secteurs actifs', value: stats?.active_sectors || 0, icon: TrendingUp, color: 'border-[#1B93A4]', link: '/clients' },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#1B2A4A] font-manrope tracking-tight">Tableau de bord</h1>
          <p className="text-gray-400 mt-1 text-sm">Vue d'ensemble de votre activité BENCHORY</p>
        </div>
        <Link
          to="/reports"
          className="bg-[#B8860B] text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-[#9A7009] transition-all shadow-md flex items-center gap-2"
          data-testid="dashboard-generate-btn"
        >
          <Target size={16} />
          Générer un rapport
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {cards.map((c) => (
          <Link
            key={c.label}
            to={c.link}
            className={`bg-white border-l-4 ${c.color} shadow-[0_2px_8px_rgba(27,42,74,0.04)] rounded-r-lg p-6 hover:shadow-lg transition-all duration-300 group`}
            data-testid={`dashboard-card-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{c.label}</p>
                <p className="text-3xl font-bold text-[#1B2A4A] mt-2 font-manrope">{c.value}</p>
              </div>
              <c.icon className="text-gray-300 group-hover:text-[#B8860B] transition-colors" size={28} />
            </div>
          </Link>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-[0_2px_8px_rgba(27,42,74,0.04)] border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-bold text-[#1B2A4A] font-manrope">Derniers rapports</h2>
          <Link to="/reports" className="text-[#B8860B] text-sm font-medium hover:underline flex items-center gap-1">
            Voir tout <ChevronRight size={14} />
          </Link>
        </div>
        {stats?.latest_reports?.length > 0 ? (
          <div className="divide-y divide-gray-50">
            {stats.latest_reports.map((r) => (
              <Link
                key={r.id}
                to={`/reports/${r.id}`}
                className="flex items-center px-6 py-4 hover:bg-gray-50/50 transition-colors"
                data-testid={`dashboard-report-${r.id}`}
              >
                <div className="w-10 h-10 rounded-lg bg-[#1B2A4A]/5 flex items-center justify-center mr-4">
                  <FileText size={18} className="text-[#1B2A4A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#1B2A4A] text-sm truncate">{r.establishment_name}</p>
                  <p className="text-xs text-gray-400">{r.period_label} — {r.segment_label}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      <span className="text-xl font-bold text-[#1B2A4A]">{r.diagnostic?.score_global || '—'}</span>
                      <span className="text-xs text-gray-400">/100</span>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-gray-300" />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="px-6 py-12 text-center text-gray-400 text-sm">
            Aucun rapport généré pour le moment
          </div>
        )}
      </div>

      {stats?.sectors?.length > 0 && (
        <div className="bg-white rounded-xl shadow-[0_2px_8px_rgba(27,42,74,0.04)] border border-gray-100 p-6">
          <h2 className="font-bold text-[#1B2A4A] font-manrope mb-4">Secteurs disponibles</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {stats.sectors.map((s) => (
              <div key={s.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50/50 border border-gray-100">
                <div className="w-8 h-8 rounded-md bg-[#B8860B]/10 flex items-center justify-center">
                  <Building2 size={16} className="text-[#B8860B]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1B2A4A]">{s.name}</p>
                  <p className="text-xs text-gray-400">{s.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
